import fetchApi from '@/lib/tools/api'
import {Producer} from '@/types'
import {CheckCircle as SuccessIcon, Error as ErrorIcon, Sync as SyncIcon} from '@mui/icons-material'
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Typography
} from '@mui/material'
import React, {useCallback, useState} from 'react'
import {useNotify, useRecordContext, useRefresh} from 'react-admin'

interface SireneSyncButtonProps {
	variant?: 'contained' | 'outlined' | 'text'
	size?: 'small' | 'medium' | 'large'
	color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success'
}

export default function SireneSyncButton({
											 variant = 'contained',
											 size = 'medium',
											 color = 'primary'
										 }: SireneSyncButtonProps) {
	const [open, setOpen] = useState(false)
	const [loading, setLoading] = useState(false)
	const [success, setSuccess] = useState(false)
	const [error, setError] = useState<string | null>(null)

	const record = useRecordContext<Producer>()
	const notify = useNotify()
	const refresh = useRefresh()

	const formatLastUpdate = (dateString?: string) => {
		if (!dateString) return 'Jamais synchronisé'
		return new Date(dateString).toLocaleDateString('fr-FR', {
			year: 'numeric',
			month: 'long',
			day: 'numeric',
			hour: '2-digit',
			minute: '2-digit'
		})
	}

	const handleClickOpen = useCallback(() => {
		setOpen(true)
		setSuccess(false)
		setError(null)
	}, [])

	const handleClose = useCallback(() => {
		setOpen(false)
		setSuccess(false)
		setError(null)
	}, [])

	const handleSync = useCallback(async () => {
		if (!record?.id) return

		setLoading(true)
		setError(null)
		setSuccess(false)

		try {
			// Call the backend API to trigger SIRENE sync for this specific producer
			const response = await fetchApi<{ success: boolean; message?: string; updated_data?: any }>(
				`/producers/${record.id}/sync-sirene`,
				{},
				null,
				'POST'
			)

			if (response.success) {
				setSuccess(true)
				notify('Synchronisation SIRENE réussie', {type: 'success'})
				refresh()

				// Auto-close after 2 seconds on success
				setTimeout(() => {
					handleClose()
				}, 2000)
			} else {
				throw new Error(response.message || 'Erreur inconnue')
			}
		} catch (err: any) {
			const errorMessage = err.message || 'Erreur lors de la synchronisation SIRENE'
			setError(errorMessage)
			notify(`Échec de la synchronisation: ${errorMessage}`, {type: 'error'})
		} finally {
			setLoading(false)
		}
	}, [record, notify, refresh, handleClose])

	if (!record) {
		return null
	}

	return (
		<>
			<Button
				variant={variant}
				color={color}
				size={size}
				startIcon={<SyncIcon/>}
				onClick={handleClickOpen}
				disabled={loading}
				sx={{
					minWidth: '200px',
					'&:hover': {
						transform: 'scale(1.02)',
						transition: 'transform 0.2s ease-in-out'
					}
				}}
			>
				Synchroniser SIRENE
			</Button>

			<Dialog
				open={open}
				onClose={loading ? undefined : handleClose}
				maxWidth="sm"
				fullWidth
				aria-labelledby="sirene-sync-dialog-title"
				disableEscapeKeyDown={loading}
			>
				<DialogTitle id="sirene-sync-dialog-title">
					<Box display="flex" alignItems="center" gap={1}>
						<SyncIcon color="primary"/>
						Synchronisation INSEE SIRENE
					</Box>
				</DialogTitle>

				<DialogContent>
					{success ? (
						<Alert severity="success" icon={<SuccessIcon/>} sx={{mb: 2}}>
							<Typography variant="body1" fontWeight="bold">
								Synchronisation réussie !
							</Typography>
							<Typography variant="body2">
								Les données SIRENE ont été mises à jour avec succès.
							</Typography>
						</Alert>
					) : error ? (
						<Alert severity="error" icon={<ErrorIcon/>} sx={{mb: 2}}>
							<Typography variant="body1" fontWeight="bold">
								Erreur de synchronisation
							</Typography>
							<Typography variant="body2">
								{error}
							</Typography>
						</Alert>
					) : (
						<>
							<Typography variant="h6" gutterBottom>
								Entreprise : {record.name}
							</Typography>
							{record.name_enterprise && (
								<Typography variant="body2" color="text.secondary" gutterBottom>
									{record.name_enterprise}
								</Typography>
							)}

							<Box sx={{my: 2}}>
								<Typography variant="body2" gutterBottom>
									<strong>Dernière synchronisation :</strong>
								</Typography>
								<Chip
									label={formatLastUpdate(record.sirene_last_update)}
									color={record.sirene_last_update ? 'success' : 'warning'}
									size="small"
									variant="outlined"
								/>
							</Box>

							{loading ? (
								<Alert severity="info" sx={{mt: 2}}>
									<Box display="flex" alignItems="center" gap={2}>
										<CircularProgress size={20}/>
										<Typography variant="body2">
											Synchronisation en cours... Cela peut prendre quelques instants.
										</Typography>
									</Box>
								</Alert>
							) : (
								<>
									<Typography variant="body2" sx={{mt: 2, mb: 1}}>
										Cette action va mettre à jour les données suivantes depuis la base SIRENE de
										l'INSEE :
									</Typography>
									<Box component="ul" sx={{pl: 2, m: 0}}>
										<Typography component="li" variant="body2">
											Statut administratif (Actif/Cessé)
										</Typography>
										<Typography component="li" variant="body2">
											Date de cessation d'activité (si applicable)
										</Typography>
										<Typography component="li" variant="body2">
											Autres informations administratives disponibles
										</Typography>
									</Box>

									<Alert severity="warning" sx={{mt: 2}}>
										<Typography variant="body2">
											<strong>Attention :</strong> Cette opération remplacera les données
											existantes
											par celles de la base SIRENE officielle.
										</Typography>
									</Alert>
								</>
							)}
						</>
					)}
				</DialogContent>

				<DialogActions sx={{px: 3, pb: 2}}>
					{success ? (
						<Button onClick={handleClose} color="primary">
							Fermer
						</Button>
					) : (
						<>
							<Button
								onClick={handleClose}
								disabled={loading}
								color="inherit"
							>
								Annuler
							</Button>
							<Button
								onClick={handleSync}
								disabled={loading}
								variant="contained"
								color="primary"
								startIcon={loading ? <CircularProgress size={20}/> : <SyncIcon/>}
							>
								{loading ? 'Synchronisation...' : 'Confirmer'}
							</Button>
						</>
					)}
				</DialogActions>
			</Dialog>
		</>
	)
}
